import React, { useMemo, useState } from 'react';
import {
  selectedOhtStatusClass,
  useOhtFleetSnapshots,
  type OhtSimulationSnapshot,
} from '../../hooks/useSelectedOhtSimulation';
import { useAppSelector } from '../../hooks/redux';
import SimulationAccordion from './SimulationAccordion';
import OhtTravelHistoryMap from './OhtTravelHistoryMap';
import { liveOhtRuntimeStore } from '../../store/liveOhtRuntimeStore';
import type { AmhsLiveWaitFrame, LiveOhtWaitState } from '../../types/amhsStream';
import { buildOhtHistoryRows, formatUtilizationPct } from '../../utils/ohtFleetMetrics';

const MAX_ROUTE_ACTIONS = 3;
type OhtDetailTab = 'details' | 'history';

const DETAIL_TABS: Array<{ id: OhtDetailTab; label: string }> = [
  { id: 'details', label: 'DETAILS' },
  { id: 'history', label: 'HISTORY' },
];

function formatOhtAlert(
  runtimeWait: LiveOhtWaitState | null | undefined,
  collision: AmhsLiveWaitFrame | undefined
): string | null {
  if (!runtimeWait && !collision) return null;

  const headline = runtimeWait
    ? runtimeWait.reason === 'junctionDwell'
      ? 'Junction dwell'
      : 'Junction wait'
    : collision?.type === 'junction_wait'
      ? collision.reason === 'junctionDwell'
        ? 'Junction dwell'
        : 'Junction wait'
      : 'Collision wait';

  const blockedBy =
    runtimeWait?.blockingOhtId ?? collision?.blockingOhtId
      ? ` · blocked by ${runtimeWait?.blockingOhtId ?? collision?.blockingOhtId}`
      : '';

  const reasonDetail =
    runtimeWait?.reason || collision?.reason
      ? ` · ${runtimeWait?.reason ?? collision?.reason}`
      : '';

  const waitDuration =
    !runtimeWait && collision?.type === 'junction_wait' ? ` · ${collision.waitSec}s` : '';

  return `${headline}${blockedBy}${reasonDetail}${waitDuration}`;
}

const OhtDetailsTab: React.FC<{ snapshot: OhtSimulationSnapshot }> = ({ snapshot }) => {
  const collision = useAppSelector((state) =>
    snapshot.ohtIds.map((id) => state.simulation.live.collisions[id]).find(Boolean)
  );
  const runtimeWait = snapshot.ohtIds
    .map((id) => liveOhtRuntimeStore.getOht(id)?.wait)
    .find(Boolean);
  const alertMessage = formatOhtAlert(runtimeWait, collision);
  const roleParts: string[] = [];
  if (snapshot.dedicatedRole) roleParts.push(snapshot.dedicatedRole);
  if (snapshot.toolType) {
    roleParts.push(
      snapshot.toToolType ? `${snapshot.toolType} → ${snapshot.toToolType}` : snapshot.toolType
    );
  }
  const roleLabel = roleParts.length > 0 ? roleParts.join(' · ') : '—';
  const routeEmptyMessage =
    snapshot.status === 'IDLE'
      ? 'OHT is idle — no active route.'
      : 'No segment path on the current leg.';
  const visibleActions = snapshot.nextActions.slice(0, MAX_ROUTE_ACTIONS);
  const hiddenActionCount = Math.max(0, snapshot.nextActions.length - visibleActions.length);

  return (
    <article className="rounded-lg border border-slate-200 p-2.5">
      <div className="flex gap-3">
        <div className="min-w-0 flex-1 space-y-2 text-[10px]">
          <div className="min-h-[2rem]" aria-live="polite">
            {alertMessage ? (
              <div className="rounded border border-red-300 bg-red-50 px-2 py-1.5 font-medium text-red-900">
                {alertMessage}
              </div>
            ) : null}
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-base font-semibold text-slate-900">{snapshot.displayName}</span>
            <span
              className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${selectedOhtStatusClass(snapshot.status)}`}
            >
              {snapshot.status}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span
              className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                snapshot.loaded ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
              }`}
            >
              {snapshot.loaded ? 'LOADED' : 'EMPTY'}
            </span>
            <span className="tabular-nums text-slate-500">
              Util {formatUtilizationPct(snapshot.utilizationPct)}
            </span>
          </div>

          <dl className="grid grid-cols-[72px_1fr] gap-x-2 gap-y-1.5">
            <dt className="text-slate-400">Location</dt>
            <dd className="truncate font-medium text-slate-700" title={snapshot.location}>
              {snapshot.location}
            </dd>
            <dt className="text-slate-400">Destination</dt>
            <dd className="truncate font-medium text-slate-700" title={snapshot.destination}>
              {snapshot.destination}
            </dd>
            <dt className="text-slate-400">ETA</dt>
            <dd className="tabular-nums font-medium text-sky-600">{snapshot.eta}</dd>
            <dt className="text-slate-400">Request</dt>
            <dd
              className="truncate font-medium text-slate-700"
              title={snapshot.requestId ?? undefined}
            >
              {snapshot.requestId ?? '—'}
            </dd>
            <dt className="text-slate-400">FOUP</dt>
            <dd className="truncate font-medium text-slate-700">{snapshot.foupId ?? '—'}</dd>
            <dt className="text-slate-400">Role</dt>
            <dd className="truncate font-medium text-slate-700" title={roleLabel}>
              {roleLabel}
            </dd>
            <dt className="text-slate-400">Phase</dt>
            <dd className="font-medium text-slate-700">{snapshot.phase}</dd>
          </dl>
        </div>

        <div className="flex w-20 shrink-0 flex-col items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-2">
          <img
            src={snapshot.graphicUrl}
            alt={snapshot.displayName}
            className="h-14 w-auto object-contain"
          />
          <span className="mt-1 text-[9px] font-semibold text-slate-500">{snapshot.displayName}</span>
        </div>
      </div>

      <div className="mt-3 min-h-[9.5rem] border-t border-slate-100 pt-3">
        <div className="space-y-3">
          <div className="min-h-[2.75rem]">
            <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
              Current path
            </p>
            {snapshot.routeNodes.length === 0 ? (
              <p className="text-[10px] text-slate-400">{routeEmptyMessage}</p>
            ) : (
              <div className="flex min-h-[1.25rem] flex-wrap items-center gap-1">
                {snapshot.routeNodes.map((node, index) => (
                  <React.Fragment key={`${node.id}-${index}`}>
                    {index > 0 ? <span className="text-[9px] text-slate-300">→</span> : null}
                    <span
                      className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                        node.active
                          ? 'bg-sky-100 text-sky-700 ring-1 ring-sky-300'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                      title={node.id}
                    >
                      {node.label}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          <div className="min-h-[3.75rem]">
            <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
              Next actions
            </p>
            {visibleActions.length === 0 ? (
              <p className="text-[10px] text-slate-400">No upcoming actions.</p>
            ) : (
              <ol className="space-y-1 text-[10px] text-slate-700">
                {visibleActions.map((action, index) => (
                  <li key={`${index}-${action}`} className="flex gap-2">
                    <span className="w-3 shrink-0 font-semibold text-slate-400">
                      {index + 1}.
                    </span>
                    <span className="line-clamp-2">{action}</span>
                  </li>
                ))}
                {hiddenActionCount > 0 ? (
                  <li className="text-[10px] text-slate-400">
                    +{hiddenActionCount} more action{hiddenActionCount === 1 ? '' : 's'}
                  </li>
                ) : null}
              </ol>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-[10px] text-slate-500">
            <span className="font-semibold text-slate-700">Leg </span>
            {snapshot.activeLeg?.fromPortId ?? '—'} → {snapshot.activeLeg?.toPortId ?? '—'}
            {snapshot.requestId ? (
              <span className="ml-2 text-slate-400">· {snapshot.requestId}</span>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
};

const OhtHistoryTab: React.FC<{ snapshot: OhtSimulationSnapshot; simTime: number }> = ({
  snapshot,
  simTime,
}) => {
  const ohtTimelines = useAppSelector((state) => state.simulation.live.ohtTimelines);
  const historyRows = useMemo(
    () => buildOhtHistoryRows(snapshot.ohtIds, ohtTimelines, simTime),
    [ohtTimelines, simTime, snapshot.ohtIds]
  );

  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
          Traveling area
        </p>
        <OhtTravelHistoryMap ohtIds={snapshot.ohtIds} simTime={simTime} />
      </div>

      <div>
        <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
          Activity log
        </p>
        {historyRows.length === 0 ? (
          <p className="py-3 text-center text-[10px] text-slate-400">
            No travel history yet — run the simulation to record OHT movement.
          </p>
        ) : (
          <ol className="max-h-40 space-y-1 overflow-y-auto text-[10px] text-slate-700 scrollbar-thin">
            {historyRows.map((row, index) => (
              <li
                key={`${row.startS}-${row.endS}-${index}`}
                className="flex items-start gap-2 rounded border border-slate-100 bg-slate-50 px-2 py-1.5"
              >
                <span className="shrink-0 tabular-nums text-slate-400">
                  {row.startS.toFixed(1)}s
                </span>
                <span className="min-w-0 flex-1">{row.label}</span>
                <span
                  className={`shrink-0 rounded px-1 py-0.5 text-[8px] font-bold uppercase ${
                    row.kind === 'travel'
                      ? 'bg-sky-100 text-sky-700'
                      : row.kind === 'transfer'
                        ? 'bg-emerald-100 text-emerald-700'
                        : row.kind === 'wait'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {row.kind}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};

/**
 * OHT fleet table + tabbed detail panel for the simulation sidebar.
 */
const SelectedOhtPanel: React.FC = () => {
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<OhtDetailTab>('details');
  const { fleetOptions, snapshots, simTime } = useOhtFleetSnapshots();

  const rows = useMemo(
    () =>
      fleetOptions.map((option) => ({
        option,
        snapshot: snapshots.find((item) => item.elementId === option.id) ?? null,
      })),
    [fleetOptions, snapshots]
  );

  const selectedSnapshot = useMemo(
    () => snapshots.find((item) => item.elementId === selectedElementId) ?? null,
    [selectedElementId, snapshots]
  );

  return (
    <div className="space-y-2">
      <SimulationAccordion
        title="OHT FLEET"
        ariaLabel="OHT fleet"
        defaultOpen
        className="flex min-h-0 flex-col"
        bodyClassName="flex min-h-0 flex-col"
      >
        <div className="min-h-0 overflow-auto px-3 pb-2 pt-1.5 scrollbar-thin">
          {rows.length === 0 ? (
            <p className="py-4 text-center text-[10px] text-slate-400">No OHTs in layout.</p>
          ) : (
            <table className="w-full table-fixed border-collapse text-left">
              <thead>
                <tr className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                  <th className="pb-2 pr-2 font-semibold">Name</th>
                  <th className="w-[5.5rem] pb-2 pr-2 font-semibold">Status</th>
                  <th className="w-[4.5rem] pb-2 font-semibold text-right">Util %</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ option, snapshot }) => {
                  const selected = selectedElementId === option.id;
                  return (
                    <tr
                      key={option.id}
                      tabIndex={0}
                      role="button"
                      aria-selected={selected}
                      onClick={() => {
                        setSelectedElementId(option.id);
                        setDetailTab('details');
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedElementId(option.id);
                          setDetailTab('details');
                        }
                      }}
                      className={`cursor-pointer border-t border-slate-100 text-[10px] transition-colors ${
                        selected
                          ? 'bg-sky-50 ring-1 ring-inset ring-sky-200'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="truncate py-2 pr-2 font-medium text-slate-800" title={option.name}>
                        {option.name}
                      </td>
                      <td
                        className={`truncate py-2 pr-2 font-semibold uppercase ${selectedOhtStatusClass(option.status)}`}
                      >
                        {option.status}
                      </td>
                      <td className="py-2 text-right tabular-nums font-medium text-slate-700">
                        {formatUtilizationPct(
                          snapshot?.utilizationPct ?? option.utilizationPct
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </SimulationAccordion>

      <SimulationAccordion
        title="OHT DETAILS"
        ariaLabel="OHT details"
        defaultOpen
        headerExtra={
          selectedSnapshot ? (
            <span className="max-w-[8rem] truncate rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold text-slate-600">
              {selectedSnapshot.displayName}
            </span>
          ) : null
        }
      >
        {!selectedSnapshot ? (
          <p className="px-3 py-4 text-center text-[10px] text-slate-400">
            Select an OHT from the table to view details.
          </p>
        ) : (
          <div className="p-3">
            <div
              className="mb-3 flex items-end gap-4 border-b border-slate-100"
              role="tablist"
              aria-label="OHT detail views"
            >
              {DETAIL_TABS.map((item) => {
                const selected = detailTab === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => setDetailTab(item.id)}
                    className={`relative pb-2 text-[10px] font-semibold tracking-wide transition-colors ${
                      selected ? 'text-sky-600' : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {item.label}
                    {selected ? (
                      <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-sky-500" />
                    ) : null}
                  </button>
                );
              })}
            </div>

            {detailTab === 'details' ? (
              <div role="tabpanel" aria-label="OHT details">
                <OhtDetailsTab snapshot={selectedSnapshot} />
              </div>
            ) : (
              <div role="tabpanel" aria-label="OHT history">
                <OhtHistoryTab snapshot={selectedSnapshot} simTime={simTime} />
              </div>
            )}
          </div>
        )}
      </SimulationAccordion>
    </div>
  );
};

export default SelectedOhtPanel;
